import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Search, Pencil, Trash2, BookOpen, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { KB_CATEGORIES } from "@/config/crm";

const EMPTY = { title: "", category: "General Information", content: "", tags: "", source: "", is_active: true };

export default function KnowledgeBase() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (search.trim()) params.search = search.trim();
    if (category !== "all") params.category = category;
    try { const { data } = await api.get("/knowledge", { params }); setItems(data.knowledge); }
    finally { setLoading(false); }
  }, [search, category]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (k) => { setEditing(k); setForm({ title: k.title, category: k.category, content: k.content, tags: (k.tags || []).join(", "), source: k.source || "", is_active: k.is_active }); setOpen(true); };

  const save = async () => {
    if (!form.title.trim() || !form.content.trim()) { toast.error("Title and content are required"); return; }
    setSaving(true);
    const payload = { ...form, tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean) };
    try {
      if (editing) await api.put(`/knowledge/${editing.id}`, payload);
      else await api.post("/knowledge", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const remove = async (k) => {
    try { await api.delete(`/knowledge/${k.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="animate-fade-up space-y-6" data-testid="knowledge-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight text-slate-900">Knowledge Base</h1>
          <p className="mt-1 text-sm text-slate-500"><span className="font-medium text-slate-700">{items.length}</span> records · powers Wellora AI answers</p>
        </div>
        <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-knowledge-button"><Plus className="h-4 w-4" /> Add Knowledge</Button>
      </div>

      <Card className="p-4 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search knowledge…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="knowledge-search" />
          </div>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="sm:w-60" data-testid="knowledge-filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Categories</SelectItem>{KB_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </Card>

      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-40" />)}</div>
      ) : items.length === 0 ? (
        <Card className="py-16 text-center text-sm text-slate-400 shadow-sm" data-testid="knowledge-empty">No knowledge records found.</Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {items.map((k) => (
            <Card key={k.id} className="flex flex-col p-5 shadow-sm" data-testid="knowledge-card">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600"><BookOpen className="h-4 w-4" /></div>
                  <div>
                    <p className="font-medium text-slate-900">{k.title}</p>
                    <Badge variant="secondary" className="mt-0.5 bg-slate-100 text-xs">{k.category}</Badge>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {!k.is_active && <Badge className="bg-slate-100 text-slate-500">Archived</Badge>}
                  <Button variant="ghost" size="icon" onClick={() => openEdit(k)} data-testid={`edit-knowledge-${k.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(k)} data-testid={`delete-knowledge-${k.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              </div>
              <p className="mt-3 line-clamp-3 text-sm text-slate-600">{k.content}</p>
              {(k.tags || []).length > 0 && <div className="mt-3 flex flex-wrap gap-1">{k.tags.map((t) => <span key={t} className="rounded bg-slate-50 px-2 py-0.5 text-xs text-slate-500">#{t}</span>)}</div>}
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="knowledge-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Knowledge" : "Add Knowledge"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="knowledge-title" /></div>
            <div className="space-y-1.5"><Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}><SelectTrigger data-testid="knowledge-category"><SelectValue /></SelectTrigger>
                <SelectContent>{KB_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label>Content</Label><Textarea rows={5} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="knowledge-content" /></div>
            <div className="space-y-1.5"><Label>Tags / keywords (comma separated)</Label><Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} data-testid="knowledge-tags" /></div>
            <div className="space-y-1.5"><Label>Source / reference</Label><Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} /></div>
            <div className="flex items-center gap-3"><Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="knowledge-active" /><Label>Active</Label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="knowledge-submit">{saving && <Loader2 className="h-4 w-4 animate-spin" />}Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
